<template>
  <div>
    <project-toolbar :selectMonth="isAzure ? true : false">
      <button
        v-if="isAzure && invoice_usages_downloader"
        class="md-button md-size-small box-right-edge"
        @click="downloadUsages"
      >
        {{ L('download') }}
      </button>
    </project-toolbar>
    <md-table
      v-model="invoiceFiltered"
      md-stripe
      :md-sort.sync="sort.sort"
      :md-sort-order.sync="sort.sortOrder"
      md-fixed-header
      :md-height="window.height"
      ref="dataTable"
    >
      <md-table-row slot="md-table-row" slot-scope="{ item }">
        <md-table-cell
          :md-label="L('seq')"
          md-sort-by="seq"
          class="invoice-no"
          >{{ item.seq }}</md-table-cell
        >
        <md-table-cell :md-label="L('name')" md-sort-by="name">{{
          item.name
        }}</md-table-cell>
        <md-table-cell
          :md-label="L('invoiceMonth')"
          md-sort-by="invoice_month"
          >{{ item.invoice_month }}</md-table-cell
        >
        <md-table-cell :md-label="L('originalCost')">
          {{
            item.original_costs
              .map((c) => currency(c.cost + c.discount, c.currency))
              .filter((c) => c !== 0)
              .join(' / ') || 0
          }}
        </md-table-cell>
        <md-table-cell :md-label="L('discount')" md-sort-by="discount">
          {{
            item.original_costs
              .map((c) => currency(c.discount, c.currency))
              .filter((c) => c !== 0)
              .join(' / ') || 0
          }}
        </md-table-cell>
        <md-table-cell
          v-if="$config.feature.showExchangeRate"
          :md-label="L('exchangeRate')"
        >
          {{ item.exchange_rate | round2 }}
        </md-table-cell>
        <md-table-cell :md-label="L('cost')">{{
          (item.final_cost - item.vat) | currency(item.charge_currency)
        }}</md-table-cell>
        <md-table-cell :md-label="L('vat')">{{
          Number(item.vat) | currency(item.charge_currency)
        }}</md-table-cell>
        <md-table-cell :md-label="L('finalCost')" md-sort-by="final_cost">{{
          item.final_cost | currency(item.charge_currency)
        }}</md-table-cell>
        <md-table-cell :md-label="L('createdDate')" md-sort-by="created_at">{{
          item.created_at | moment_format('YYYY-MM-DD')
        }}</md-table-cell>
        <md-table-cell :md-label="L('action')" class="action-button">
          <md-button
            class="md-icon-button md-dense"
            @click="downloadInvoice(item)"
          >
            <fa-icon icon="file-alt" />
            <md-tooltip>{{ L('viewInvoice') }} (PDF)</md-tooltip>
          </md-button>
          <md-button
            class="md-icon-button md-dense"
            @click="downloadUsage(item)"
            v-if="!isFromBillOne(item) || item.usage_csv_url"
          >
            <fa-icon icon="download" />
            <md-tooltip>{{ L('downloadUsage') }} (CSV)</md-tooltip>
          </md-button>
          <md-button class="md-icon-button md-dense" @click="viewUsage(item)">
            <fa-icon icon="file-invoice" />
            <md-tooltip>{{ L('viewUsage') }}</md-tooltip>
          </md-button>
          <md-button
            class="md-icon-button md-dense"
            @click="viewReport(item)"
            v-if="
              item.report_pdf_url ||
              ($config.feature.report &&
                (item.report || []).length > 0 &&
                item.is_report)
            "
          >
            <fa-icon icon="file-contract" />
            <md-tooltip>{{ L('viewReport') }}</md-tooltip>
          </md-button>
        </md-table-cell>
      </md-table-row>
    </md-table>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import api from '@/api'
import billone from '@/api/billone'
import ProjectToolbar from '@/views/billing/ProjectToolbar'
import { propFilter } from '@/lib/helper'
import { formatDate } from '@/lib/dateutil'
import store from '@/store'

// 빌원으로 인보이스를 이관한 CSP. 여기 없는 CSP 는 예전처럼 hb2 인보이스만 본다.
const BILLONE_ENABLED_PLATFORMS = ['gcp', 'azure']

export default {
  components: { ProjectToolbar },

  data: () => ({
    invoice: {
      list: null,
      search: '',
      searchFields: ['seq'],
    },
    invoices: [],
    locale: 'en',

    window: {
      height: 0,
    },

    sort: {
      sort: 'seq',
      sortOrder: 'desc',
    },
  }),

  mounted() {
    this.load()
  },

  computed: {
    ...mapState({
      userInfo: (state) => state.userInfo,
      curMonth: (state) => state.curMonth,
    }),

    invoiceFiltered: {
      get() {
        return propFilter(this.invoice)
      },
      set() {},
    },

    invoiceMonth() {
      return this.curMonth.replace('-', '')
    },

    isAzure() {
      return this.$config.platform.id === 'azure'
    },

    // 빌원 인보이스를 쓰는 CSP 목록 (BO-2957 → BO-3682 에서 azure 추가).
    //
    // 이 프론트 하나가 CSP 6개를 돌리므로 가두지 않으면 아직 이관하지 않은 CSP
    // (tencent·alibaba·akamai·oci)에도 빌원 조회가 나간다. **화이트리스트로 두는** 이유는
    // `=== 'gcp' || === 'azure'` 처럼 늘려 가면 CSP 를 더할 때 조건을 빠뜨리기 쉬워서다.
    //
    // 이관한 CSP 를 추가할 때는 백엔드 hb2 API(`/hb2/{csp}/billing/v1/invoices`)와
    // 그 CSP 의 `invoice.billone.firstMonth` 가 함께 준비돼 있어야 한다.
    isBilloneEnabled() {
      return BILLONE_ENABLED_PLATFORMS.includes(this.$config.platform.id)
    },

    invoice_usages_downloader() {
      return this.userInfo.invoice_usages_downloader
    },
  },

  watch: {
    userInfo() {
      this.load()
    },

    invoiceMonth() {
      this.load()
    },
  },

  methods: {
    load() {
      if (!this.userInfo.seq) {
        return
      }

      // hb2 예전 인보이스와 빌원 신규 인보이스를 합쳐서 보여준다 (과도기).
      // 빌원은 hb2 의 사용자 번호를 모른다. 이메일로 조회한다.
      // GCP 가 아니면 빌원을 부르지 않는다 — 빈 배열이라 아래는 hb2 만 처리한다 (BO-2957).
      Promise.all([
        api.user.invoice(this.userInfo.seq),
        this.isBilloneEnabled
          ? billone.invoice(this.userInfo.email).catch(() => [])
          : [],
      ]).then(([hb2Data, billoneData]) => {
        // 번호는 거르기 전 hb2 목록을 기준으로 매긴다 — 감춘 건도 번호는
        // 이미 쓰였으므로, 거른 뒤 기준으로 잡으면 빌원 번호가 그것과 겹친다.
        const merged = [
          ...this.hideMigrated(hb2Data),
          ...this.numberBillOne(hb2Data, billoneData),
        ]
        let formedData = merged.map((d) => {
          d.seq = Number(d.seq)
          d.original_cost = Number(d.original_cost)
          d.discount = Number(d.discount)
          d.final_cost = Number(d.final_cost)
          d.logo = this.getLogoName(d.logo)
          d.original_costs.sort((a, b) => a.currency.localeCompare(b.currency))
          return d
        })
        if (this.invoiceMonth && this.isAzure) {
          formedData = formedData.filter(
            (d) => d.invoice_month === this.invoiceMonth
          )
        }

        this.$refs.dataTable.mdSortFn(formedData)
        this.invoice.list = formedData
      })
    },

    // 빌원이 발행하기 시작한 달부터는 hb2 발행분을 감춘다.
    // 그러지 않으면 같은 달 인보이스가 두 번 보인다 — 빌원 쪽은 billone.js 가
    // fromMonth 로 거르고, hb2 쪽은 여기서 거른다. 기준월은 같은 설정값이다.
    //
    // GCP 가 아니면 빌원이 없으므로 그대로 다 보여준다. 기준월이 없는
    // 환경(스테이징)도 마찬가지다 — 검증용이라 양쪽을 다 봐야 한다.
    hideMigrated(hb2Data) {
      const month = (this.$config.invoice.billone || {}).firstMonth
      if (!this.isBilloneEnabled || !month) {
        return hb2Data
      }
      // 설정은 2026-08, hb2 의 invoice_month 는 202608 이다.
      const from = String(month).replace('-', '')
      return hb2Data.filter((d) => String(d.invoice_month) < from)
    },

    // 빌원 건에는 hb2 번호가 없다. hb2 마지막 번호 뒤로 이어서 붙인다.
    // 청구 월이 이른 것부터 주어, hb2 처럼 번호가 시간 순으로 늘어나게 한다.
    numberBillOne(hb2Data, billoneData) {
      const lastSeq = hb2Data.reduce(
        (max, d) => Math.max(max, Number(d.seq) || 0),
        0
      )
      return [...billoneData]
        .sort(
          (a, b) =>
            String(a.invoice_month).localeCompare(String(b.invoice_month)) ||
            String(a.billone_invoice_no).localeCompare(
              String(b.billone_invoice_no)
            )
        )
        .map((d, i) => Object.assign(d, { seq: lastSeq + 1 + i }))
    },

    // 빌원에서 온 건인지. 다운로드 링크가 있으면 빌원 것이다.
    isFromBillOne(item) {
      return Boolean(
        item.invoice_pdf_url || item.usage_csv_url || item.report_pdf_url
      )
    },

    downloadUsage(item) {
      if (item.usage_csv_url) {
        // hb2 는 「번호.csv」 로 준다. 빌원 파일 이름도 화면에 보이는 번호로 맞춘다.
        billone.download(item.usage_csv_url, `${item.seq}.csv`).catch((e) => {
          store.dispatch('dialog/showAlert', e.message)
        })
        return
      }
      api.download.invoiceUsage(item.seq, this.userInfo.seq)
    },

    downloadUsages() {
      const seqs = this.invoice.list.map((i) => i.seq)
      if (!seqs.length) {
        store.dispatch('dialog/showAlert', this.L('noData'))
        return
      }
      api.download.invoiceUsages(this.invoiceMonth, seqs)
    },

    downloadInvoice(item) {
      if (item.invoice_pdf_url) {
        window.open(item.invoice_pdf_url, '_blank')
        return
      }

      const company = item.company ? item.company : ''
      const invoiceTitle = this.$config.invoice.title
      const settingInfo = JSON.parse(localStorage.getItem('settingInfo')) || {}
      const month =
        settingInfo.language === 'ko'
          ? `${Number(item.invoice_month.substring(4))}${this.L('month')}`
          : formatDate(item.invoice_month, 'MMMM')
      const name = `${item.logo}_${company}_${invoiceTitle}_${month}_${this.L(
        'invoice'
      )}.pdf`
      api.download.invoice(item.seq, name, this.userInfo.seq)
    },

    viewUsage(item) {
      // 빌원 건은 화면 번호(invoiceNo)가 아니라 내부 id 로 열어야 한다.
      // 빌원 이용내역 API 가 invoiceId 를 받기 때문이다.
      const key = item.billone_invoice_id || item.seq
      window.open(
        `/${this.userInfo.seq}/invoice_detail/${key}/${this.locale}`,
        '_blank'
      )
    },

    windowResized() {
      this.window.height = window.innerHeight - 240
    },

    viewReport(item) {
      if (item.report_pdf_url) {
        window.open(item.report_pdf_url, '_blank')
        return
      }

      if (!item.is_report) {
        store.dispatch('dialog/showAlert', this.L('notFoundReport'))
      } else {
        const company = item.company ? item.company : ''
        const invoiceTitle = this.$config.invoice.title
        const settingInfo =
          JSON.parse(localStorage.getItem('settingInfo')) || {}
        const month =
          settingInfo.language === 'ko'
            ? `${item.invoice_month.substring(0, 4)}년_${Number(
                item.invoice_month.substring(4)
              )}${this.L('month')}`
            : formatDate(item.invoice_month, 'MMMM_YYYY')
        const name = `${item.logo}_${company}_${invoiceTitle}_${month}_${this.L(
          'report'
        )}.pdf`

        api.download.report(item.seq, name, this.userInfo.seq)
      }
    },

    getLogoName(logo) {
      let name = this.L('megazone')
      if (logo === 'megazone-soft') name = this.L('megazoneSoft')
      else if (logo === 'megazone-cloud') name = this.L('megazoneCloud')
      return name
    },
  },
}
</script>

<style lang="scss" scoped>
.md-content {
  padding: 16px;
}

// 빌원 번호는 'INV-2026-54807' 처럼 길어서 그냥 두면 두 줄로 잘린다.
.invoice-no,
.invoice-no ::v-deep .md-table-cell-container {
  white-space: nowrap;
}
</style>
